import {
    Card,
    Icon,
    Tag,
    Typography,
    Checkbox,
    ButtonMenu,
    CircularProgress,
} from "@ann/web-components";
import {
    CardWrapper,
    CardTop,
    CardHeader,
    CardHeaderTitle,
    CardMeta,
    CardMetaItem,
} from "./Card.styled";
import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { observer } from "mobx-react";
import { ModalBase } from "../../../../components/Modal";

//  PERFORMANCE: observer for MobX reactivity, memo for performance
const DriveDocumentCard = observer(({ uuid, name, selectMode, onSelect, selectedItems, removeAccessToDocument, removeAccessLoading, setShowSlideoutDocumentDetail, setSelectedDocument }) => {
    const [showRemoveAccessModal, setShowRemoveAccessModal] = useState(false);
    const [removingAccess, setRemovingAccess] = useState(false);

    //  PERFORMANCE: Memoized selection check
    const isSelected = useMemo(() => {
        return selectedItems.includes(uuid);
    }, [selectedItems, uuid]);

    //  PERFORMANCE: Memoized callbacks
    const handleRemoveAccess = useCallback(() => {
        setRemovingAccess(true);
        setShowRemoveAccessModal(false);
        removeAccessToDocument(uuid);
    }, [removeAccessToDocument, uuid]);

    const handleRemoveAccessClick = useCallback(() => {
        setShowRemoveAccessModal(true);
    }, []);

    const handleCheckboxChange = useCallback(() => {
        onSelect(uuid);
    }, [onSelect, uuid]);

    const handleMenuChange = useCallback((value) => {
        if (value === 'remove-access') {
            handleRemoveAccessClick();
        } else if (value === 'view-details') {
            setSelectedDocument({
                uuid,
                title: name
            });
            setShowSlideoutDocumentDetail(true);
        }
    }, [handleRemoveAccessClick, setShowSlideoutDocumentDetail, setSelectedDocument, uuid, name]);

    //  PERFORMANCE: Fixed useEffect - only depends on specific values
    useEffect(() => {
        setRemovingAccess(removeAccessLoading && isSelected);
    }, [removeAccessLoading, isSelected]);

    return (
        <>
            <Card disabled={removingAccess}>
                <CardWrapper>
                    <CardTop>
                        <CardHeader>
                            {selectMode && (
                                <Checkbox
                                    onChange={handleCheckboxChange}
                                    size="medium"
                                    checked={isSelected}
                                />
                            )}
                            <Icon name="description" size="medium" />
                            <CardHeaderTitle>
                                <Typography category="subtitle" size="medium" weight="bold" color="title">
                                    {name}
                                </Typography>
                            </CardHeaderTitle>
                            {removingAccess ? (
                                <CircularProgress />
                            ) : (
                                <ButtonMenu
                                    customIcon="dotsMenu"
                                    disableEffects
                                    iconPosition="right"
                                    label=""
                                    menuPosition="bottom"
                                    onChange={handleMenuChange}
                                    size="medium"
                                    type="tertiary"
                                    width="260px"
                                >
                                    <ButtonMenu.Menu>
                                        <ButtonMenu.Item
                                            label="Remove access"
                                            value="remove-access"
                                        />
                                        <ButtonMenu.Item
                                            label="View details"
                                            value="view-details"
                                        />
                                    </ButtonMenu.Menu>
                                </ButtonMenu>
                            )}
                        </CardHeader>

                        <Typography
                            category="body"
                            size="medium"
                            weight="regular"
                            color="body"
                        >
                            Example description of the content that could go inside.
                        </Typography>
                    </CardTop>

                    <CardMeta>
                        <CardMetaItem>
                            <Tag icon="person" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                jane doe
                            </Typography>
                        </CardMetaItem>


                        <CardMetaItem>
                            <Tag icon="calendarToday" size="medium" color="informative" />
                            <Typography category="body" size="small" weight="regular" color="subtitle">
                                2025-08-10
                            </Typography>
                        </CardMetaItem>
                    </CardMeta>
                </CardWrapper>
            </Card>
            {showRemoveAccessModal && (
                <ModalBase
                    isOpen={showRemoveAccessModal}
                    onClose={() => setShowRemoveAccessModal(false)}
                    onClick={handleRemoveAccess}
                    title={`Are you sure you want to remove access?`}
                    description={"Your agent won’t be able to use it for responses until you reconnect it."}
                    buttonTitle="Remove access"
                    modalWidth="571px" />
            )}
        </>
    )
});

DriveDocumentCard.displayName = 'DriveDocumentCard';

export default memo(DriveDocumentCard);
